import * as React from 'react';
import styled from 'styled-components';

import { ListView } from '@longline/aqua-ui/controls/ListView/ListView';
import { SVG } from '@longline/aqua-ui/svg';
import { Icon } from '@longline/aqua-ui/controls/Icon';
import { Key } from '@longline/aqua-ui/controls/Key';
import { NumberFormatter } from '@longline/aqua-ui/formatters/NumberFormatter';
import { SecondaryButton } from '@longline/aqua-ui/controls/SecondaryButton';
import { Message } from '@longline/aqua-ui/containers/Message';

import { useGlobalState } from '../../contexts/state/GlobalStateContext';
import { ILayer } from '../../types/ILayer';
import { LayerInfoBox } from '../../infoboxes/LayerInfoBox';
import { LayerCard } from './LayerCard';
import { useConfirm } from '../../contexts/confirm/ConfirmContext';
import { Guide } from '../../ui/Guide';
import { EyeIcon } from '../../infoboxes/EyeIcon';
import { Pane } from '../../ui/Pane';
import { Can } from '../../ui/Can';
import { LAYER_GROUP_INFO, layerGroupFor, TLayerGroup } from '../../types/ELayerGroup';

const LayerPane = () => {
  const confirm = useConfirm();
  const globalState = useGlobalState();

  const handleDeleteAll = () => {
    globalState.clearLayers();
  }

  return (
    <>
      {/* Title & Import button */}
      <Pane.Heading>
        <Pane.Title>Layers </Pane.Title>
      </Pane.Heading>
      <Pane.Subheader expandable action={<Can permission="import_layer"><SecondaryButton title="Import" positive icon={SVG.Icons.Plus} onClick={globalState.startImport}/></Can>}>
        <Can permission="import_layer">Import layer</Can>
      </Pane.Subheader>
      {/* Layer list */}
      <ListView 
        dark 
        data={globalState.layers} 
        onClick={(layer) => globalState.setActiveLayer(layer)} 
        shadow
        active={globalState.activeLayer}
        aux={<LayerInfoBox/>}
        total
        singular="layer"
        plural="layers"
        noData={<Message margin>There are no layers yet.</Message>}
        rowHeight={56}
        decorator={(layer: ILayer) => <GroupBand $group={layerGroupFor(layer.type)}/>}
      >
        <ListView.Column paddingLeft={32} active width={3} name="Name">{(item: ILayer) => <LayerCard layer={item}/>}</ListView.Column>
        <ListView.Column active={globalState.expanded} width="80px" name="Points" align="right">{(item: ILayer) => <NumberFormatter value={item.data.length} decimals={0}/>}</ListView.Column>
        <ListView.Column active width="74px" name="">{(item: ILayer) => <EyeIcon visible={item.visible} onClick={() => globalState.toggleLayer(item)}/>}</ListView.Column>
      </ListView>
      {globalState.layers.length > 0 && 
        <Pane.Fab negative title="Delete all layers" icon={SVG.Icons.Delete} onClick={() => confirm.start(handleDeleteAll, "Delete all layers", "Are you sure you wish to delete all layers?")}/>}

      {globalState.activeLayer == null && globalState.selectedStructures.length === 0 &&
        <Pane.Guide>
          <Guide x={0} y={0} title="Layer management">
            <ul>
              <li>Click <Key value="+"/> to create a new layer from an Excel file.</li>
            </ul>
          </Guide>
        </Pane.Guide>}
      {globalState.activeLayer != null && globalState.selectedStructures.length === 0 &&
        <Pane.Guide>
          <Guide x={0} y={0} title="Editing a layer">
            <ul>
              <li>Click <Icon url={SVG.Icons.Eye}/> to show or hide a layer.</li>
              <li>Click <Icon url={SVG.Icons.Delete}/> to remove a layer from the map.</li>
              <li>Edit contours and gradient to change layer visualization.</li>
            </ul>
          </Guide>
        </Pane.Guide>}
    </>
  )
}

/**
 * Colour band marking which group a layer belongs to. Absolutely positioned
 * over the row, as ListView's decorator contract requires, and click-through
 * so it doesn't swallow row selection.
 */
const GroupBand = styled.div<{ $group: TLayerGroup }>`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-color: ${p => LAYER_GROUP_INFO[p.$group].tint};
  border-left: 4px solid ${p => LAYER_GROUP_INFO[p.$group].edge};
`

export { LayerPane }
